import { QuestionAnswered, CandidateReport } from '../apis/entities/report.entity'
import { Chart } from 'react-google-charts'
import { Assessment } from '../apis/entities/assessment.entity'
import ReactLoading from 'react-loading'
import { Footer } from './AssessmentIndividualReport'

type AssessmentIndividualReportProps = {
  candidateId: string
  assessment: Assessment
  data: CandidateReport
  testDate: string
  logo: string
  showAllOutcomes: boolean
}

export default function ReportContent1({
  candidateId,
  assessment,
  data,
  testDate,
  logo,
  showAllOutcomes,
}: AssessmentIndividualReportProps) {
  const levels = data.courses.map((course) => course.level)
  const maxLevel = Math.max(...(levels ?? [0]))
  const minLevel = Math.min(...(levels ?? [0]))

  interface Outcome {
    name: string
    level: number
    result: boolean | null
  }
  const answeredQuestionsByLevel: QuestionAnswered[][] =
    levels?.map((level) => []) ?? []
  data?.answeredQuestions.forEach((question) => {
    if (!answeredQuestionsByLevel[question.level - 1]) {
      answeredQuestionsByLevel[question.level - 1] = []
    }
    answeredQuestionsByLevel[question.level - 1].push(question)
  })
  console.log('answeredQuestionsByLevel', answeredQuestionsByLevel)

  const outcomesByLevel: Outcome[][] = levels?.map(() => []) ?? []
  assessment?.questions.forEach((question) => {
    if (!outcomesByLevel[question.level - 1]) {
      outcomesByLevel[question.level - 1] = []
    }

    // Find the outcomes list for the current level
    const outcomesForLevel = outcomesByLevel[question.level - 1]

    // Check if an outcome with the same name already exists in the outcomes list
    const existingOutcomeIndex = outcomesForLevel.findIndex(
      (outcome) => outcome.name === question.learningOutcome,
    )

    if (existingOutcomeIndex === -1) {
      // If the outcome doesn't exist, add it to the outcomes list
      outcomesForLevel.push({
        name: question.learningOutcome,
        level: question.level,
        result:
          data?.answeredQuestions.find(
            (answeredQuestion) => answeredQuestion.questionId === question.id,
          )?.isCorrect ?? null,
      })
    } else {
      // If the outcome exists, update its result if needed
      // TODO calculate % for multiple questions in the same learning outcomes
      const existingOutcome = outcomesForLevel[existingOutcomeIndex]
      const newResult =
        data?.answeredQuestions.find(
          (answeredQuestion) => answeredQuestion.questionId === question.id,
        )?.isCorrect ?? null
      if (newResult !== null) {
        existingOutcome.result = newResult
      }
      // override old result if new result is false
      // if (newResult === false) {
      //   existingOutcome.result = newResult
      // }
    }
  })

  console.log('outcomesByLevel', outcomesByLevel)

  const correctAnswerRatesByLevel = answeredQuestionsByLevel
    .filter((levelQuestions) => levelQuestions.length > 0)
    .map((levelQuestions) => {
      return {
        level: levelQuestions[0].level,
        rate:
          Math.round(
            (100 *
              levelQuestions.filter(
                (answeredQuestion) => answeredQuestion.isCorrect,
              ).length) /
              levelQuestions.length,
          ) / 100,
        passRate:
          (assessment?.classifiedLevels.length ?? 0) > 0
            ? assessment?.classifiedLevels.find(
                (levelItem) => levelItem.level === levelQuestions[0].level,
              )?.upThreshold ?? Infinity
            : assessment?.courses.find(
                (course) => course.level === levelQuestions[0].level,
              )?.passScore ?? Infinity,
      }
    })
  console.log('correctAnswerRatesByLevel', correctAnswerRatesByLevel)

  const correctAnswerDescriptions = correctAnswerRatesByLevel.map((level) => {
    return `Level ${level.level} correct rate: ${100 * level.rate}%`
  })

  const rectifiedChartValue = (value: number): number => {
    const minCourseLevel =
      assessment?.courses.reduce(
        (min, course) => (course.level < min ? course.level : min),
        assessment?.courses[0].level,
      ) ?? 1
    const maxCourseLevel =
      assessment?.courses.reduce(
        (max, course) => (course.level > max ? course.level : max),
        assessment?.courses[0].level,
      ) ?? 1
    if (value < minCourseLevel) {
      return minCourseLevel - 0.5
    }
    if (value > maxCourseLevel) {
      return maxCourseLevel + 0.5
    }
    return value
  }

  const correctAnswerSummary = correctAnswerDescriptions.join(', ')
  console.log('correctAnswerSummary', correctAnswerSummary)

  const recommendationByCourseLevel: { level: number; pass: boolean }[] = []
  data?.courses.forEach((course) => {
    let pass: boolean = false

    const assessedResult = correctAnswerRatesByLevel.find(
      (level) => level.level === course.level,
    )
    if (assessedResult) {
      pass = assessedResult.rate >= assessedResult.passRate
    } else {
      pass = (data.finalLevel ?? 1) > course.level
    }

    recommendationByCourseLevel.push({ level: course.level, pass: pass })
  })
  console.log('recommendationByCourseLevel', recommendationByCourseLevel)

  let topLevel = minLevel
  recommendationByCourseLevel.forEach((element) => {
    if (element.pass && element.level > topLevel) {
      topLevel = element.level
    }
  })
  const topLevelName = assessment?.courses.find(
    (course) => course.level === topLevel,
  )?.levelName
  console.log('assessment?.courses', assessment?.courses)
  console.log('minLevel', minLevel)
  console.log('topLevelName', topLevelName)
  console.log('topLevel', topLevel)

  let answerData: any[] = [
    ['Question', 'Correct', 'Incorrect', 'Candidate level', 'Summary'],
  ]
  answerData.push(
    ...(data?.answeredQuestions
      ? data.answeredQuestions?.map((question) => {
          return [
            {
              v: +question.questionNumber,
              f: `Question no.${question.questionNumber} is ${
                question.isCorrect ? 'correct' : 'incorrect'
              }`,
            },

            {
              v: question.isCorrect
                ? rectifiedChartValue(+question.level)
                : null,
              f: `Overall level ${
                question.level
              } correct answer rate: ${Math.round(
                (100 *
                  answeredQuestionsByLevel[question.level - 1].filter(
                    (answeredQuestion) => answeredQuestion.isCorrect,
                  ).length) /
                  answeredQuestionsByLevel[question.level - 1].length,
              )}%`,
            },
            {
              v: question.isCorrect
                ? null
                : rectifiedChartValue(+question.level),
              f: `Overall level ${
                question.level
              } correct answer rate: ${Math.round(
                (100 *
                  answeredQuestionsByLevel[question.level - 1].filter(
                    (answeredQuestion) => answeredQuestion.isCorrect,
                  ).length) /
                  answeredQuestionsByLevel[question.level - 1].length,
              )}%`,
            },
            {
              v: rectifiedChartValue(+question.currentLevel),
              f: `${question.currentLevel}`,
            },
            null,
          ]
        })
      : []),
  )

  //final level
  answerData.push([
    { v: answerData.length, f: `Final level: ${data?.finalLevel}` },
    rectifiedChartValue(data?.finalLevel ?? 0),
    rectifiedChartValue(data?.finalLevel ?? 0),
    rectifiedChartValue(data?.finalLevel ?? 0),
    {
      v: rectifiedChartValue(data?.finalLevel ?? 0),
      f: data?.answeredQuestions ? correctAnswerSummary : '',
    },
  ])
  // if ((assessment?.classifiedLevels.length ?? 0) === 0) {
  //   answerData = answerData.map(([question, correct, incorrect]) => [question, correct, incorrect]);
  // }
  console.log('answerData', answerData)

  const ticksV = []
  for (let i = 1; i <= maxLevel; i++) {
    ticksV.push(i)
  }
  const ticksH = []
  for (let i = 0; i <= (data?.answeredQuestions.length ?? 0); i = i + 1) {
    ticksH.push(i)
  }
  const answerDataOptions = {
    series: {
      0: {
        type: 'scatter',
        color: 'blue',
      },
      1: {
        type: 'scatter',
        color: 'red',
      },
      2: {
        type: 'line',
        // pointSize: 4,
        // pointShape: 'circle', //'circle', 'triangle', 'square', 'diamond', 'star', or 'polygon'.
        color: 'purple',
      },
      3: {
        type: 'scatter',
        pointSize: 16,
        pointShape: 'star', //'circle', 'triangle', 'square', 'diamond', 'star', or 'polygon'.
        color: 'orange',
      },
    },
    // trendlines: {
    //   2: {
    //     type: "exponential",
    //     visibleInLegend: true,
    //   },
    // },
    hAxis: {
      title: 'Question number',
      titleTextStyle: { color: '#FFF' }, // Color of the axis title
      textStyle: { color: '#FFF' }, // Color of axis labels
      gridlines: { color: 'transparent' }, //, interval: 1}, // Color of gridlines
      minorGridlines: { color: '#FFF', interval: 3.5 }, // Color of minor gridlines
      baselineColor: '#FFF',
      minValue: 0,
      maxValue: (data?.answeredQuestions.length ?? 0) + 1,
      ticks: ticksH,
    },
    vAxis: {
      title: 'Question level',
      minValue: 0,
      maxValue: maxLevel + 1,
      ticks: ticksV,
      titleTextStyle: { color: '#FFF' }, // Color of the axis title
      textStyle: { color: '#FFF' }, // Color of axis labels
      gridlines: { color: 'transparent' }, // Color of gridlines
      baselineColor: '#FFF',
    },
    legend: {
      textStyle: { color: '#FFF' }, // Change color of the legend labels
      position: 'top',
      alignment: 'end',
    },
    // trendlines: {
    //   0: {
    //     color: '#888888',
    //     tooltip: 'Custom Trendline Tooltip'
    //   }
    // },
    height: 1000,
    backgroundColor: 'transparent',
    chartArea: {
      top: 40,
      left: 100,
      bottom: 100,
      right: 100,
    },
  }

  const answerPercentageData: any[] = [
    ['Level', 'Correct answers', 'Incorrect answers'],
  ]
  answerPercentageData.push(
    ...(answeredQuestionsByLevel
      ? answeredQuestionsByLevel?.map((levelQuestions, index) => {
          return [
            `${data?.courses[index].title}`,
            levelQuestions.reduce(
              (sum, question) => sum + (question.isCorrect ? 1 : 0),
              0,
            ),
            levelQuestions.reduce(
              (sum, question) => sum + (question.isCorrect ? 0 : 1),
              0,
            ),
          ]
        })
      : []),
  )

  const answerPercentageOptions = {
    // orientation: 'vertical',
    series: {
      0: {
        color: '#1AC987',
        visibleInLegend: true,
      },
      1: {
        color: '#fff5c3',
        visibleInLegend: true,
      },
    },
    isStacked: 'percent',
    bar: {
      groupWidth: '37%',
    },
    annotations: {
      stem: {
        color: '#FFDC00',
        length: 0,
      },
      textStyle: {
        color: '#FFDC00',
      },
    },
    hAxis: {
      title: 'Answer correct rate(%)',
      titleTextStyle: { color: '#303030', fontName: 'Clancy' }, // Color of the axis title
      textStyle: { color: '#303030', fontName: 'Clancy' }, // Color of axis labels
      gridlines: { color: 'transparent' }, //, interval: 1}, // Color of gridlines
      // minorGridlines: { color: '#FFF', interval: 3.5 }, // Color of minor gridlines
      baselineColor: '#303030',
      minValue: 0,
      // maxValue: (data?.answeredQuestions.length ?? 0) + 1,
      // ticks: ticksH
      viewWindow: {
        min: -0.01,
        max: 1,
      },
    },
    vAxis: {
      title: 'Area of competency',
      // minValue: 0,
      // maxValue: maxLevel + 1,
      // ticks: ticksV,
      // ticks: [0, .3, .6, .9, 1],
      titleTextStyle: { color: '#303030', fontName: 'Clancy' }, // Color of the axis title
      textStyle: { color: '#303030', fontName: 'Clancy' }, // Color of axis labels
      gridlines: { color: 'transparent' }, // Color of gridlines
      baselineColor: '#303030',
      textPosition: 'out',
    },
    legend: {
      textStyle: { color: '#303030', fontName: 'Clancy' }, // Change color of the legend labels
      position: 'top',
      alignment: 'center', //start, center, end
    },
    height: 280,
    width: 750,
    backgroundColor: 'transparent', // Set the background color to transparent
    chartArea: {
      // backgroundColor: 'transparent', // Set chart area background to transparent
      top: 20,
      left: 140,
      bottom: 60,
      right: 20,
    },
  }

  const timeData: any[] = [['Question', 'Correct', 'Incorrect']]
  timeData.push(
    ...(data?.answeredQuestions
      ? data.answeredQuestions?.map((question) => {
          return [
            `${question.questionNumber}`,
            question.isCorrect
              ? Math.round(+question.duration * 100) / 100
              : null,
            question.isCorrect
              ? null
              : Math.round(+question.duration * 100) / 100,
          ]
        })
      : []),
  )
  console.log('timeData', timeData)

  const timeDataOptions = {
    // orientation: 'vertical',
    // bar: {groupWidth: "100%"},
    isStacked: true,
    hAxis: {
      title: 'Question number',
      titleTextStyle: { color: '#FFF' }, // Color of the axis title
      textStyle: { color: '#FFF' }, // Color of axis labels
      gridlines: { color: 'transparent' }, //, interval: 1}, // Color of gridlines
      // minorGridlines: { color: '#FFF', interval: 3.5 }, // Color of minor gridlines
      baselineColor: '#FFF',
      minValue: 0,
      // maxValue: (data?.answeredQuestions.length ?? 0) + 1,
      // ticks: ticksH
    },
    vAxis: {
      title: 'Time taken (s)',
      // minValue: 0,
      // maxValue: maxLevel + 1,
      // ticks: ticksV,
      titleTextStyle: { color: '#FFF' }, // Color of the axis title
      textStyle: { color: '#FFF' }, // Color of axis labels
      gridlines: { color: 'transparent' }, // Color of gridlines
      baselineColor: '#FFF',
    },
    legend: {
      textStyle: { color: '#FFF' }, // Change color of the legend labels
      position: 'top',
      alignment: 'end',
    },
    height: 300,
    backgroundColor: 'transparent', // Set the background color to transparent
    chartArea: {
      // backgroundColor: 'transparent', // Set chart area background to transparent
      top: 40,
      left: 100,
      bottom: 100,
      right: 100,
    },
  }

  const totalCorrectRate =
    (data?.answeredQuestions.filter((question) => question.isCorrect).length ??
      0) / (data?.answeredQuestions.length ?? 1)

  let passedOutcomes: Outcome[] = []
  let failedOutcomes: Outcome[] = []
  outcomesByLevel.forEach((levelOutcomes) => {
    levelOutcomes.forEach((outcome) => {
      if (outcome.result === false) {
        failedOutcomes.push(outcome)
      }
      if (outcome.result === true) {
        passedOutcomes.push(outcome)
      }
    })
  })

  // const maxDisplayedOutcomes = 12
  const prioritizedPassedOutcomes = 4 //trim first
  const prioritizedFailedOutcomes = 8 //prioritized
  let selectedPassedOutcomes = passedOutcomes.length
  let selectedFailedOutcomes = failedOutcomes.length

  //check excess passed and trim
  if (selectedPassedOutcomes > prioritizedPassedOutcomes) {
    //trim or use all failed
    if (selectedFailedOutcomes > prioritizedFailedOutcomes) {
      //too much failed
      selectedFailedOutcomes = prioritizedFailedOutcomes //trim failed
    }
    selectedPassedOutcomes =
      prioritizedFailedOutcomes +
      prioritizedPassedOutcomes -
      selectedFailedOutcomes //fill the rest with passed
  } else {
    //not enough passed, keep all passed
    selectedFailedOutcomes =
      prioritizedFailedOutcomes +
      prioritizedPassedOutcomes -
      selectedPassedOutcomes //fill the rest with failed
  }

  passedOutcomes = passedOutcomes.splice(-selectedPassedOutcomes)
  failedOutcomes = failedOutcomes.splice(0, selectedFailedOutcomes)

  return (
    <div className="flex grow relative w-[794px] h-[1142px] overflow-auto border-0 pl-2 pr-2 pt-3">
      <div className="flex grow flex-col gap-8 text-left">
        {data === undefined && (
          <ReactLoading
            className="fixed top-[50%]"
            type={'spinningBubbles'}
            color={'#eeeeee'}
            height={'64px'}
            width={'64px'}
          />
        )}

        {data && (
          <div className="">
            <div className="relative">
              <div className="flex justify-between items-center gap-6">
                <div className="self-start">
                  <img src={logo} alt="logo" className="w-[300px]" />

                  <div className="text-2xl font-bold pt-7">
                    {assessment?.title}
                  </div>
                  <div className="pt-3 font-roboto pb-6">
                    {assessment?.description}
                  </div>
                </div>
                <div className="flex flex-col gap-3">
                  <div className="text-[32px] font-bold">
                    {data?.firstName + ' ' + data?.lastName ?? 'Candidate name'}{' '}
                  </div>
                  <div className="text-sm font-semibold font-roboto">
                    Test date: {testDate}
                  </div>
                  <div className="text-sm font-semibold font-roboto">
                    Result code: {data?.id}
                  </div>
                  {/* summary */}
                  <div className="flex flex-col h-38 text-center overflow-hidden border border-1 border-primaryUnsw mt-5 w-[300px]">
                    <div className="font-bold flex items-center justify-center text-tertiaryTeal text-[32px] py-3">
                      Your level
                    </div>
                    <div
                      className=""
                      style={{
                        background: 'rgba(255, 220, 0, 0.30)',
                      }}
                    >
                      <div className="text-[32px] text-center font-bold py-9">
                        {topLevelName ?? topLevel}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="">
                <div className="text-2xl font-bold">Score details</div>
                <div className="flex justify-between gap-8 h-[265px] pt-6">
                  <Chart
                    chartType="BarChart"
                    data={answerPercentageData}
                    options={answerPercentageOptions}
                  />
                </div>
              </div>
              <div className="pt-6">
                <div className="text-2xl font-bold">Recommended courses</div>
                {/* courses */}
                <div className="flex flex-row space-x-2 pt-6 relative">
                  {data?.courses.map((course, index) => (
                    <div
                      className="relative min-h-full w-full max-h-[130px] font-roboto"
                      key={'course-' + index}
                    >
                      <div
                        key={index}
                        className={
                          'border border-[#1AC987] rounded-lg px-4 py-2 relative h-full' +
                          (recommendationByCourseLevel.find(
                            (recommendation) =>
                              recommendation.level === course.level,
                          )?.pass
                            ? ' opacity-50'
                            : '')
                        }
                      >
                        <div className="text-sm font-bold font-clancy">
                          {index + 1}. {course.title}
                        </div>
                        <div className="text-xs mt-2 line-clamp-4">
                          {course.description}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* end courses */}

                <div className="">
                  <div className="rounded-lg overflow-hidden border border-[#1AC987] mt-2 font-roboto">
                    <table className="w-full">
                      <tbody>
                        {passedOutcomes.length > 0 && (
                          <tr className="border">
                            <td className="w-[15%] border p-2 text-xs font-clancy">
                              Your answers indicate you can:
                            </td>
                            <td className="pl-2">
                              {passedOutcomes.map((outcome, index) => (
                                <div
                                  className="flex flex-row space-x-2 py-1 relative"
                                  key={'outcome-' + index}
                                >
                                  <div className="relative min-h-full">
                                    <div className="text-xs line-clamp-1">
                                      <li>{outcome.name}</li>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </td>
                          </tr>
                        )}
                        {failedOutcomes.length > 0 ? (
                          <tr className="border">
                            <td className="w-[15%] border p-2 text-xs font-clancy">
                              Learning opportunities:
                            </td>
                            <td className="pl-2">
                              {failedOutcomes.map((outcome, index) => (
                                <div
                                  className="flex flex-row space-x-2 py-1 relative"
                                  key={'outcome-' + index}
                                >
                                  <div className="relative min-h-full">
                                    <div className="text-xs line-clamp-1">
                                      <li>{outcome.name}</li>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </div>

                <Footer />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
